/**
 * Protected route component for WMS DAOUXUN
 * Protects routes based on authentication, roles and permissions
 * Glassmorphism interface with advanced features
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.helpers.js';
import { useTranslation } from 'react-i18next';

/**
 * ProtectedRoute - Protected route with advanced access control
 * 
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Content to protect
 * @param {string[]} props.requiredRoles - Roles required to access the route
 * @param {string[]} props.requiredPermissions - Permissions required to access the route
 * @param {boolean} props.requireAllRoles - If true, user must have ALL roles (default: false)
 * @param {boolean} props.requireAllPermissions - If true, user must have ALL permissions (default: false)
 * @param {string} props.redirectTo - Redirect URL when access is denied (default: /unauthorized)
 */
const ProtectedRoute = ({ 
  children, 
  requiredRoles = [], 
  requiredPermissions = [],
  requireAllRoles = false,
  requireAllPermissions = false,
  redirectTo = '/unauthorized',
  _showLoadingScreen = false,
  fallbackComponent = null
}) => {
  const { t } = useTranslation();
  const [loadingTimeout, setLoadingTimeout] = React.useState(false);

  const { isAuthenticated, user, checkRole, checkPermission, isLoading } = useAuth();
  const location = useLocation();

  // Add timeout to prevent infinite loading - redirect to login after 5 seconds
  React.useEffect(() => {
    let timer;
    if (isLoading || isAuthenticated === null) {
      timer = setTimeout(() => {
        setLoadingTimeout(true);
      }, 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLoading, isAuthenticated]);

  // If loading timed out, redirect to login
  if (loadingTimeout && (isLoading || isAuthenticated === null)) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show loading screen when auth state is unknown or still loading
  if (isLoading || isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 shadow-2xl p-8 max-w-md w-full text-center">
          {/* Loading animation */}
          <div className="w-20 h-20 mx-auto mb-6 relative">
            <div className="absolute inset-0 border-4 border-white/20 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-transparent border-t-blue-400 rounded-full animate-spin"></div>
            <div className="absolute inset-2 border-4 border-transparent border-t-purple-400 rounded-full animate-spin animation-delay-150"></div>
            <div className="absolute inset-4 border-4 border-transparent border-t-cyan-400 rounded-full animate-spin animation-delay-300"></div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-white mb-4">
            {t('auth.accessVerification', 'Access Verification')}
          </h2>

          {/* Status message */}
          <p className="text-white/80 mb-6">
            {t('auth.validatingPermissions', 'Validating your permissions...')}
          </p>

          {/* Progress indicators */}
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/70">{t('auth.authentication', 'Authentication')}</span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-300">{t('auth.verified', 'Verified')}</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/70">{t('auth.permissions', 'Permissions')}</span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <span className="text-blue-300">{t('auth.inProgress', 'In Progress...')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving destination
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check required roles
  if (requiredRoles.length > 0 && user) {
    const hasRequiredRoles = requireAllRoles
      ? requiredRoles.every(role => checkRole(role))
      : requiredRoles.some(role => checkRole(role));
    
    if (!hasRequiredRoles) {
      // Use fallback component if provided
      if (fallbackComponent) {
        return (
          <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
            <div className="bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 shadow-2xl p-8 max-w-2xl w-full">
              {fallbackComponent}
            </div>
          </div>
        );
      }
      
      // Otherwise, redirect
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Check required permissions
  if (requiredPermissions.length > 0 && user) {
    const hasRequiredPermissions = requireAllPermissions
      ? requiredPermissions.every(permission => checkPermission(permission))
      : requiredPermissions.some(permission => checkPermission(permission));
    
    if (!hasRequiredPermissions) {
      // Use fallback component if provided
      if (fallbackComponent) {
        return (
          <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
            <div className="bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 shadow-2xl p-8 max-w-2xl w-full">
              {fallbackComponent}
            </div>
          </div>
        );
      }
      
      // Otherwise, redirect
      return <Navigate to={redirectTo} replace />;
    }
  }

  // All checks passed, render protected content
  return <>{children}</>;
};

export default React.memo(ProtectedRoute);

